'use client';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MemberClient } from '../../services/apiClient';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';

const client = new MemberClient(API_BASE_URL, axiosInstance);

type Status = 'loading' | 'redirecting' | 'already-opted-in' | 'error';

export default function PartnerServiceRedirect() {
    const { partnerId, memberId } = useParams<{ partnerId: string; memberId: string }>();
    const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';

    const [status, setStatus] = useState<Status>('loading');
    const [optInUrl, setOptInUrl] = useState<string | null>(null);
    const [partnerName, setPartnerName] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!partnerId || !memberId) {
            setStatus('error');
            setErrorMessage('Invalid link — missing required parameters.');
            return;
        }

        client
            .activateMemberPartnerOptIn({ memberId, partnerId })
            .then((res) => {
                const url = res?.data?.optInUrl;
                const name = res?.data?.partnerName ?? 'the partner';
                const alreadyOptedIn = res?.data?.alreadyOptedIn;

                if (!url) {
                    setStatus('error');
                    setErrorMessage('No redirect URL configured for this partner.');
                    return;
                }

                setOptInUrl(url);
                setPartnerName(name);

                if (alreadyOptedIn) {
                    setStatus('already-opted-in');
                } else {
                    setStatus('redirecting');
                    setTimeout(() => {
                        window.location.href = url;
                    }, 3000);
                }
            })
            .catch(() => {
                setStatus('error');
                setErrorMessage('This link is invalid or could not be processed. Please contact us if you need assistance.');
            });
    }, [partnerId, memberId]);

    return (
        <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
            <div
                className="absolute inset-0 w-full min-h-full bg-cover bg-center -z-10"
                style={{ backgroundImage: `url(${backgroundImage})` }}
            />

            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg text-center z-10">
                <img
                    src="/images/logo-linnear-navy.png"
                    alt="Natshoot Logo"
                    className="mx-auto mb-6 w-56"
                />

                {status === 'loading' && (
                    <p className="text-gray-600">Verifying your details...</p>
                )}

                {status === 'redirecting' && optInUrl && (
                    <div>
                        <h1 className="text-xl font-semibold mb-2">You're being redirected</h1>
                        <p className="text-gray-600 mb-4">You'll be taken to the {partnerName} registration page shortly.</p>
                        <button
                            className="px-4 py-2 rounded text-white font-semibold"
                            style={{ backgroundColor: '#061139' }}
                            onClick={() => { window.location.href = optInUrl; }}
                        >
                            Go now
                        </button>
                    </div>
                )}

                {status === 'already-opted-in' && optInUrl && (
                    <div>
                        <h1 className="text-xl font-semibold mb-2">You've already opted in</h1>
                        <p className="text-gray-600 mb-4">
                            You have already completed your opt-in for <strong>{partnerName}</strong>.
                            You can still visit their website using the button below.
                        </p>
                        <button
                            className="px-4 py-2 rounded text-white font-semibold"
                            style={{ backgroundColor: '#061139' }}
                            onClick={() => { window.location.href = optInUrl; }}
                        >
                            Visit {partnerName}
                        </button>
                    </div>
                )}

                {status === 'error' && (
                    <div>
                        <h1 className="text-xl font-semibold mb-2 text-red-600">Something went wrong</h1>
                        <p className="text-gray-600">{errorMessage}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
